import { computed, inject } from '@angular/core';
import {
	patchState,
	signalStore,
	withComputed,
	withHooks,
	withMethods,
	withState,
} from '@ngrx/signals';
import { pipe } from 'rxjs';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap } from 'rxjs';
import {
	LeadStatusesClient,
	LeadStatusListDto,
	PagedDataDtoOfLeadStatusListDto,
	PagedRequestDto,
} from 'app/core/services/api.service';
import { tapResponse } from '@ngrx/operators';
import { IPillBadgeMenu } from 'app/components/badge/pill-badge/pill-badge.component';

type LeadStatusState = {
	leadStatuses: LeadStatusListDto[];
};

const initialState: LeadStatusState = {
	leadStatuses: [],
};

export const LeadStatusesStore = signalStore(
	{ providedIn: 'root' },

	withState(initialState),

	withComputed((store) => {
		return {
			newLeadStatus: computed(() =>
				store.leadStatuses().find((status) => status.name == 'New Lead')
			),
			convertedToClaim: computed(() =>
				store
					.leadStatuses()
					.find((status) => status.name == 'Converted to Claim')
			),
			leadStatusById: computed(
				() => (id: number | null) =>
					id
						? store
								.leadStatuses()
								.find((status) => status.id === id)
						: null
			),
			leadStatusPillBadges: computed(() => {
				return store.leadStatuses().map((status) => {
					let { name, color, textColor } = status;
					return {
						text: name,
						color,
						textColor,
						data: status,
					} as IPillBadgeMenu;
				});
			}),
		};
	}),

	withMethods((store) => {
		const _leadStatusesClient = inject(LeadStatusesClient);

		return {
			loadLeadStatuses: rxMethod<void>(
				pipe(
					switchMap(() => {
						let dto = PagedRequestDto.fromJS({
							pageSize: 25,
							page: 0,
							sort: 'order',
							sortDirection: 'asc',
						});

						return _leadStatusesClient.getList(dto);
					}),
					tapResponse({
						next: (statuses: PagedDataDtoOfLeadStatusListDto) =>
							patchState(store, {
								leadStatuses: statuses.records,
							}),
						error: (error) => {
							console.error('Error loading lead statuses', error);
						},
					})
				)
			),
			initializeLeadStatuses: (leadStatuses: LeadStatusListDto[]) =>
				patchState(store, { leadStatuses }),
			signOff: () => patchState(store, initialState),
		};
	}),

	withHooks({
		onInit(store) {},
	})
);
